import { Injectable, computed, signal } from '@angular/core';
import { AuthUser, PersistedToken } from '@fy-web-2/data-access-auth';
import { UserModule } from '@fy-web-2/shared-util';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error';

export interface AuthSnapshot {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  readonly token: PersistedToken | null;
  readonly userModules: readonly UserModule[];
  readonly error: string | null;
}

const initialState: AuthSnapshot = {
  status: 'idle',
  user: null,
  token: null,
  userModules: [],
  error: null,
};

/**
 * Signal-based auth store. Holds session state (token, user, user_modules,
 * status, error). Mutators are called by `AuthFacade` only; UI reads via the
 * read-only signals.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly state = signal<AuthSnapshot>(initialState);

  readonly status = computed(() => this.state().status);
  readonly user = computed(() => this.state().user);
  readonly token = computed(() => this.state().token);
  readonly userModules = computed(() => this.state().userModules);
  readonly accessToken = computed(() => this.state().token?.accessToken ?? null);
  readonly error = computed(() => this.state().error);

  readonly isAuthenticated = computed(() => {
    const token = this.state().token;
    if (!token) return false;
    return token.expiresAt > Date.now();
  });

  setLoading(): void {
    this.state.update((s) => ({ ...s, status: 'loading', error: null }));
  }

  setAuthenticated(token: PersistedToken, user: AuthUser, userModules: readonly UserModule[] = []): void {
    this.state.set({ status: 'authenticated', user, token, userModules, error: null });
  }

  setUserModules(userModules: readonly UserModule[]): void {
    this.state.update((s) => ({ ...s, userModules }));
  }

  setError(message: string): void {
    this.state.update((s) => ({ ...s, status: 'error', error: message }));
  }

  reset(): void {
    this.state.set(initialState);
  }
}
